import { of, zip } from 'rxjs'
import { map, catchError, tap, skip, switchMap } from 'rxjs/operators'
import { List, fromJS } from 'immutable'
import qs from 'query-string'

import { flog, pluck } from '@zambezi/caballo-vivo'
import createRequest$ from '@apiEnabler/ob-common/lib/request'
import {
  renewSessionToken$,
  userAuthNMethod$,
} from '@apiEnabler/ob-common/lib/intents'
import {
  parseCustomerPayloads,
  parsePayload,
} from '@apiEnabler/ob-common/lib/consent-utils'
import { parseActivities } from '@apiEnabler/ob-common/lib/activities'
import { parseConsentAudits } from '@apiEnabler/ob-common/lib/consents'
import { getClientNameText } from '@apiEnabler/ob-common/src/tpps'
import {
  dateNDaysInPast,
  currentDate,
} from '@apiEnabler/ob-common/src/date-utils'

import { staffPortalSearchQuery$ } from './staff'
import { brand$ } from './brand'
import { payMeYouConfig$ } from './paymeyou-config'

const PAGE_SIZE = 8
const DAYS_OF_HISTORY = 90

export function createCancelConsent$(consent) {
  return staffPortalSearchQuery$.pipe(
    switchMap(staffPortalSearchQuery =>
      createRequest$(`consents/${consent.id}${staffPortalSearchQuery}`, 'PUT', {
        status: 'WITHDRAWN',
      }).pipe(
        flog(`Withdraw consent ${consent.id} result`),
        catchError(error => {
          throw Object.assign(new Error(error), {
            type: 'revoke-consent',
            clientName: getClientNameText(
              consent.clientName,
              consent.agentName,
              consent.channelId
            ),
          })
        }),
        tap(renewSessionToken$),
        map(parsePayload)
      )
    )
  )
}

export const getCustomerDetails$ = staffPortalSearchQuery$.pipe(
  switchMap(staffPortalSearchQuery =>
    createRequest$('pbbcustomer' + staffPortalSearchQuery).pipe(
      tap(emitUserAuthNMethod),
      flog('Get customer response'),
      map(parseCustomerPayloads)
    )
  )
)

export const createGetPaymentHistory$ = paymentId =>
  staffPortalSearchQuery$.pipe(
    switchMap(staffPortalSearchQuery =>
      createRequest$(
        `consents/${paymentId}/activityAudits${staffPortalSearchQuery}`
      ).pipe(flog(`Get history for payment ${paymentId} result`))
    )
  )

export const createGetActivities$ = consentId =>
  staffPortalSearchQuery$.pipe(
    switchMap(staffPortalSearchQuery =>
      createRequest$(
        `consents/${consentId}/activities${staffPortalSearchQuery}`
      ).pipe(
        flog(`Get activities for consent ${consentId}`),
        map(parseActivities)
      )
    )
  )

export const createGetConsentAudits$ = consentId =>
  staffPortalSearchQuery$.pipe(
    switchMap(staffPortalSearchQuery =>
      createRequest$(
        `consents/${consentId}/audits${staffPortalSearchQuery}`
      ).pipe(
        flog(`Get consent audits for ${consentId} response`),
        catchError(() => of(List())), // Endpoint may not be deployed
        map(parseConsentAudits)
      )
    )
  )

function emitUserAuthNMethod(payload) {
  userAuthNMethod$.next(payload.getIn(['customer', 'userAuthNMethod']))
}

const customerCIN$ = staffPortalSearchQuery$.pipe(
  switchMap(staffPortalSearchQuery =>
    of(qs.parse(staffPortalSearchQuery).cin || '')
  )
)

function withCin(postRequest$, customerCIN) {
  return function (configObj) {
    return postRequest$(customerCIN, configObj).pipe(flog('Request with CIN'))
  }
}

function createPostPayMeRequests$(postRequest$, customerCIN) {
  return payMeYouConfig$.pipe(
    switchMap(configList => {
      return zip(...configList.map(withCin(postRequest$, customerCIN))).pipe(
        map(fromJS),
        map(x => x.flatten(1))
      )
    })
  )
}

//Requests for the Pay Me service
export const createPostPayMeList$ = () => {
  return customerCIN$.pipe(
    switchMap(customerCIN =>
      createPostPayMeRequests$(createPostPayMePagedReq$, customerCIN)
    ),
    flog('PayMe links final list')
  )
}

function createPostPayMePagedReq$(customerCIN, { brandId, companyId }) {
  return createPagedRequest$(null, [])

  function pagingOfLinks$(continuationToken, links) {
    if (!continuationToken) return of(links)

    return createPagedRequest$(continuationToken, links)
  }

  function createPagedRequest$(continuationToken, links) {
    return createRequest$('paymelinks', 'POST', {
      criteria: [
        {
          name: 'ExternalReference',
          value: customerCIN,
        },
      ],
      paginationDetails: {
        continuationToken,
        pageSize: PAGE_SIZE,
      },
      brandId,
      companyId,
      startDate: dateNDaysInPast(DAYS_OF_HISTORY),
    }).pipe(
      flog(`Paged request made`),
      switchMap(response =>
        pagingOfLinks$(
          response.toJS().paginationDetails.continuationToken,
          links.concat(response.toJS().linkDetailsList)
        )
      )
    )
  }
}

export const createPostPayMeReusableList$ = () => {
  return customerCIN$.pipe(
    switchMap(customerCIN =>
      createPostPayMeRequests$(createPostPayMeReusablePagedReq$, customerCIN)
    ),
    flog('PayMe reusable links final list')
  )
}

function createPostPayMeReusablePagedReq$(customerCIN, { brandId, companyId }) {
  return createPagedReusableRequest$(null, [])

  function pagingOfReusableLinks$(continuationToken, links) {
    if (!continuationToken) return of(links)

    return createPagedReusableRequest$(continuationToken, links)
  }

  function createPagedReusableRequest$(continuationToken, links) {
    return createRequest$('reusablepaymelinks', 'POST', {
      criteria: [
        {
          name: 'ExternalReference',
          value: customerCIN,
        },
      ],
      paginationDetails: {
        continuationToken,
        pageSize: PAGE_SIZE,
      },
      brandId,
      companyId,
      startDate: dateNDaysInPast(DAYS_OF_HISTORY),
    }).pipe(
      switchMap(response =>
        pagingOfReusableLinks$(
          response.toJS().paginationDetails.continuationToken,
          links.concat(response.toJS().linkDetailsList)
        )
      )
    )
  }
}

export function createDeletePayMeCancelLink$(linkId, companyId, brandId) {
  return zip(customerCIN$, brand$).pipe(
    switchMap(([customerCIN, bankwideBrand]) =>
      createRequest$(
        `${bankwideBrand.get('id')}/${customerCIN}/paymelinks/${linkId}`,
        'PUT',
        {
          companyId,
          brandId,
        }
      ).pipe(
        flog(`Cancel PayMe link ${linkId}`),
        skip(),
        catchError(error => {
          throw Object.assign(new Error(error), {
            type: 'cancel-link',
            linkId: linkId,
          })
        })
      )
    )
  )
}

export function createDeletePayMeReusableCancelLink$(
  linkId,
  companyId,
  brandId
) {
  return zip(customerCIN$, brand$).pipe(
    switchMap(([customerCIN, bankwideBrand]) =>
      createRequest$(
        `${bankwideBrand.get(
          'id'
        )}/${customerCIN}/reusablepaymelinks/${linkId}`,
        'PUT',
        {
          companyId,
          brandId,
        }
      ).pipe(
        flog(`Cancel PayMe Reusable link ${linkId}`),
        skip(),
        catchError(error => {
          throw Object.assign(new Error(error), {
            type: 'cancel-link',
            linkId: linkId,
          })
        })
      )
    )
  )
}

//Requests for the PayYou Service
export const createPostPayYouList$ = () => {
  return customerCIN$.pipe(
    switchMap(customerCIN => createPostPayYouListRequests$(customerCIN))
  )
}

function createPostPayYouListRequests$(customerCIN) {
  return createPagedPayYouRequest$(null, [])

  function pagingOfPayYouLinks$(index, links) {
    if (!index) return of(fromJS(links))

    return createPagedPayYouRequest$(index, links)
  }

  function createPagedPayYouRequest$(index, links) {
    return createRequest$('payyoulinks', 'POST', {
      paginationDetails: {
        pageIndex: index,
        pageSize: PAGE_SIZE,
      },
      cin: customerCIN,
      fromDate: dateNDaysInPast(DAYS_OF_HISTORY),
      toDate: currentDate(),
    }).pipe(
      switchMap(response =>
        pagingOfPayYouLinks$(
          response.toJS().paginationDetails.pageIndex,
          links.concat(response.toJS().payoutHistory)
        )
      )
    )
  }
}

export const createGetPayYouLinkDetails$ = linkId =>
  createRequest$(`payyoulinks/${linkId}`).pipe(
    pluck('payoutStatus'),
    flog(
      'Get PayYou Link Details',
      x => x.toJS(),
      catchError(error => {
        throw Object.assign(new Error(error), {
          type: 'link-not-found',
        })
      })
    )
  )

export function createPutPayYouCancelLink$(linkId) {
  return zip(customerCIN$, brand$).pipe(
    switchMap(([customerCIN, bankwideBrand]) =>
      createRequest$(
        `${bankwideBrand.get(
          'id'
        )}/${customerCIN}/payyoulinks/${linkId}/cancel`,
        'PUT'
      ).pipe(
        flog(`Cancel PayYou link ${linkId}`),
        skip(),
        catchError(error => {
          throw Object.assign(new Error(error), {
            type: 'cancel-link',
            linkId: linkId,
          })
        })
      )
    )
  )
}
